import React, { useCallback, useContext, useMemo, useState } from "react";
import { UserCardPopup } from "../../components/common/UserCardPopup";

/** Minimal identity an avatar can hand over to open the card. */
export interface UserCardTarget {
  id: string;
  username?: string;
  avatar?: string;
}

export interface UserCardContextState {
  open: (user: UserCardTarget) => void;
  close: () => void;
}

const UserCardContext = React.createContext<UserCardContextState | undefined>(undefined);

/**
 * Holds the single user card of the app: any avatar can open it, wherever it
 * sits in the tree. Outside a meeting the card degrades to the identity alone.
 */
export const UserCardProvider = ({ children }: { children: React.ReactNode }) => {
  const [target, setTarget] = useState<UserCardTarget | undefined>();

  const open = useCallback((user: UserCardTarget) => setTarget(user), []);
  const close = useCallback(() => setTarget(undefined), []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <UserCardContext.Provider value={value}>
      {children}
      {target && <UserCardPopup user={target} onClose={close} />}
    </UserCardContext.Provider>
  );
};

/** Undefined outside the provider, so avatars can stay inert there. */
export const useUserCard = (): UserCardContextState | undefined => useContext(UserCardContext);
